/**
 * Employee module help — feature reference for screens in Employee.jsx.
 * Describe WHAT exists and WHERE to configure it — not step-by-step process flows.
 * When adding a new Employee screen or settings area, add a topic here + snippet + RBAC in catalog.js.
 */
export const EMPLOYEE_HELP_TOPICS = [
  {
    id: "dashboard",
    title: "Dashboard",
    summary: "Headcount and workforce snapshot widgets for the Employee module.",
    location: "Employee → Dashboard",
    features: [
      "Summary cards: total headcount, active employees, on-leave count, and new joiners.",
      "Trend and comparison metrics shown against active headcount where applicable.",
      "Entry point to other Employee areas (list, onboarding, settings) via the left navigation.",
    ],
    snippet: "dashboard",
  },
  {
    id: "employees-list",
    title: "Employees List",
    summary: "Searchable directory of all employees with filters, columns, and row actions.",
    location: "Employee → Employee → Employees",
    features: [
      "Search by employee code, name, or mobile number.",
      "Status filters (Active, Probation, Confirmed, Resigned, Relieved, etc.) and advanced filters (department, branch, type).",
      "Table columns include code, name, department, designation, status, profile completion %, and more (resizable).",
      "Row menu (⋮): View, Edit, Confirmation, Transfer, Resignation — availability depends on role access.",
      "Create Employee button — direct hire without onboarding workflow (separate from Onboard Employee).",
      "Profile completion column shows % complete across org, personal, family, bank, education, profession, statutory, and documents sections.",
    ],
    snippet: "employees-list",
  },
  {
    id: "employee-profile-sections",
    title: "Employee Profile Sections",
    summary: "Tabs and data areas in the employee create/edit form (direct or onboarding).",
    location: "Employee form → tab bar",
    features: [
      "Org Details — code, name, branch, department, sub-department, designation, grade, category, reporting manager, DOJ, employment type/status, contact, statutory IDs, CTC. Eye icon on CTC opens Profile Salary Structure (statutory applicability, Employer NPS / VPF / Meal Voucher opt-ins, breakdown). Export Excel / Export PDF include amounts and calculation basis.",
      "Personal — marital status, present & permanent address, emergency contact.",
      "Family — family members; orphan / no-family flags when applicable.",
      "Bank — for joined employees, one grid lists all accounts (full account numbers) including current; Effective Date uses the standard date picker. Update bank details requires confirm account number (masks while confirming) and Bank Proof (Documents). Soft note to contact HR/Admin if the pay period is already closed. Admins can delete a history row. Onboarding still uses the simple bank fields only.",
      "Education — qualifications (degree, institution, year, etc.).",
      "Profession — prior work experience (company, role, dates).",
      "Statutory Details — UAN / ESI numbers and related IDs. PF / ESI / PT / Gratuity applicability flags live under Profile Salary Structure (CTC eye), not Org Details.",
      "Documents — upload and manage document categories (Aadhaar, PAN, photo, Bank Proof, etc.). Bank Proof must be uploaded/replaced when bank details change for joined employees.",
      "Profile completion % is calculated from these sections; incomplete sections are listed in the completion indicator.",
      "In onboarding mode, Org Details must be saved before other tabs unlock.",
    ],
    snippet: "employee-profile-sections",
  },
  {
    id: "create-employee",
    title: "Create Employee (Direct)",
    summary: "Modal/form to add an employee immediately — no onboarding policy or approval chain.",
    location: "Employees list → Create Employee",
    features: [
      "Opens the full employee form in create mode (all profile tabs).",
      "Employee code: auto-generated when an automated code structure is active; otherwise entered manually.",
      "ESS / Send Invite options follow General Settings (Enable ESS, default invite).",
      "Saves directly to the employee directory — does not create an onboarding request or insights row.",
      "Distinct from Onboard Employee, which uses policies, approvals, and checklist configuration.",
    ],
    snippet: "create-employee",
  },
  {
    id: "onboard-employee",
    title: "Onboard Employee",
    summary: "Policy-based candidate form — draft record, org details, and linked onboarding request.",
    location: "Employee → Onboard Employee (left nav)",
    features: [
      "Same tabbed profile form as direct create, but tied to an onboarding request and applicable Onboarding Policy.",
      "Org Details tab saves a draft candidate (candidate ID assigned; not yet a completed hire).",
      "Applicable policy is matched from Settings → Policies → Onboarding (branch, department, grade, etc.).",
      "Employee Insights lists onboarding requests and checklist tasks for this flow.",
      "Approval method, checklist tasks, and document requirements come from the matched policy — configure under Settings → Policies.",
      "Profile completion % applies to the candidate/employee record linked to the request.",
    ],
    snippet: "onboard-employee",
  },
  {
    id: "employee-lifecycle",
    title: "Lifecycle Actions",
    summary: "Confirmation, transfer, and resignation from Employees → Actions.",
    location: "Employee → Employees → Actions (cards)",
    features: [
      "Toggle Employees / Actions on the Employees screen — Employees is the default list; Actions shows cards.",
      "Open a card → search employee by code or name → Continue opens the same form as before.",
      "Confirmation — move employee from Probation to Confirmed; uses Confirmation Policy from Settings.",
      "Transfer — policy-matched request with workflow approval and checklist (Employee Insights).",
      "Resignation — same New Resignation form as Request → Resignation (policy, notice shortfall, checklist; hints under the ⓘ icon).",
      "Each action may appear in Employee Insights and Quick Panel approvals depending on policy workflow.",
      "Visibility of each action is controlled in User Roles → Screen Access (Employee module).",
    ],
    snippet: "employee-lifecycle",
  },
  {
    id: "lifecycle-approvals-desk",
    title: "Lifecycle Approvals (Onboarding → Exit)",
    summary:
      "End-to-end: configure policy, raise resignation from Request (or HR from Employee), track status, act under Approvals, oversee in Activity Console.",
    location: "Quick Panel → Request / Approvals · top bar Activity Console",
    features: [
      "Configure Onboarding, Confirmation, Transfer, Separation policies under Employee → Settings → Policies (approval method, workflow steps, checklist).",
      "Resignation: raise from Request → Resignation or Employees → Actions → Resignation (same policy form; shortfall/checklist details in the ⓘ info icon). Confirmation / Transfer / Onboarding still raise from Employee.",
      "Quick Panel → Request shows your own lifecycle request status (Pending / Approved / Rejected), policy name, stage, and pending with. Onboarding shows a profile card; if you are the assignee or Admin, Approve / Reject is available there too.",
      "Quick Panel → Approvals lists items assigned to you from the policy workflow. Open a row for the candidate profile card and Approve / Reject. Admin / Super Admin can act on any pending step (actor name is captured).",
      "Onboarding and checklist detail shows only key fields (like Leave): type/department/date of join for approvals, and checklist name + due date for tasks. Employee name and code stay on the header.",
      "Each checklist item appears once. Reject removes it from Pending and keeps it on Rejected — it does not come back when the profile is complete.",
      "Activity Console (top bar) is the company-wide desk that replaced Employee Insights — pending onboardings, approvals, and checklist actions.",
      "auto_approved policies complete without Approvals rows; workflow policies create pending Approvals for the current role.",
      "ESS Quick Panel also has Request + Approvals for managers and assignees (RBAC: Quick Panel screens).",
    ],
    snippet: "lifecycle-approvals-desk",
  },
  {
    id: "employee-insights",
    title: "Activity Console (company desk)",
    summary:
      "Company-wide desk for onboarding and lifecycle items — status, assignee, profile, and Approve / Reject. Replaces the old Employee Insights panel.",
    location: "Top bar → Activity Console",
    features: [
      "Lists live API rows for Onboarding, Confirmation, Transfer, Resignation / Termination, plus attendance/helpdesk where loaded.",
      "Onboard Employee (Org + Reporting Manager) sends the request straight to RM approval — it appears here and under Approvals with Approve / Reject.",
      "Onboarding Task rows are one per checklist item. Reject moves the task to Rejected; Done marks it complete. Detail form matches Leave (key fields only).",
      "Approvals sidebar is the personal inbox for the RM; Activity Console gives Admins company-wide visibility and the same Approve / Reject actions.",
      "Complements Quick Panel Approvals (personal inbox) with company-wide visibility for admins.",
    ],
    snippet: "employee-insights",
  },
  {
    id: "import",
    title: "Import",
    summary: "Bulk employee upload and bulk exit via Excel templates.",
    location: "Employee → Import, or Import / Export → Employee",
    features: [
      "Download Excel template with mandatory and optional column headers.",
      "Upload filled file; system validates rows before import.",
      "Supports org, contact, statutory flags, ESS invite columns per template.",
      "Import Confirmed flow for bulk creation of active employees (separate confirmation step in UI).",
      "Validation errors are shown per row; successful rows create employee records in bulk.",
      "Bulk Employees Exit (Import / Export): Relieved or Terminated only — Emp Code, Name, Type of Exit, Resignation Date (required for Relieved), Relieved Date, Reason from Masters; blocked if attendance or payroll month for Relieved Date is locked/finalised. Updates employee profile status and dates.",
    ],
    snippet: "import",
  },
  {
    id: "reports",
    title: "Reports",
    summary: "Standard HR reports with filters and export from the Reports submenu.",
    location: "Employee → Reports",
    features: [
      "Employee Report — master employee listing with filters.",
      "Age Report — age distribution / demographics.",
      "Service Report — tenure and service length.",
      "Salary Report — compensation summary.",
      "Confirmation Report — probation confirmations.",
      "Transfers Report — transfer history.",
      "Resignations Report — resignation and exit records.",
      "Salary Revision Report lives under Payroll → Reports (not Employee Reports).",
      "Each report supports filter criteria shown on the report screen and download/export where available.",
    ],
    snippet: "reports",
  },
  {
    id: "export",
    title: "Export",
    summary: "Download employee data by section for selected employees.",
    location: "Employee → Export",
    features: [
      "Organization Details — branch, department, designation, manager, DOJ, status, etc.",
      "Personal Details — address, emergency contact, marital status.",
      "Family Details — dependents and family member records.",
      "Bank Details — account and IFSC information.",
      "Education Details — qualifications.",
      "Professional Details — prior experience.",
      "Statutory Details — UAN / ESI numbers. Applicability flags are on Profile Salary Structure (CTC eye).",
      "All Employee Data — combined export across sections.",
      "Select employees from the list on the export screen, then download the chosen section.",
    ],
    snippet: "export",
  },
  {
    id: "settings-general",
    title: "Settings — General",
    summary: "Company-wide ESS defaults for new employees.",
    location: "Employee → Settings → General Settings",
    features: [
      "Enable ESS — Yes/No: whether employees can use the self-service portal.",
      "Enable ESS by default — when ESS is on, new employees have Send Invite checked by default on create.",
      "Save persists to company settings (affects Create Employee and import invite behaviour).",
    ],
    snippet: "settings-general",
  },
  {
    id: "settings-employee-code",
    title: "Settings — Employee Code",
    summary: "Manual vs automated employee code patterns and history.",
    location: "Employee → Settings → Employee Code",
    features: [
      "Pattern type: Manual — HR enters each code; Automated — system assigns next code from prefix + sequence.",
      "Automated: prefix, digit count, starting number, current count; preview of next code.",
      "Manual: guidance for free-text codes on create/onboarding.",
      "View History — audit of code structure changes.",
      "Active structure determines behaviour on Create Employee, Onboard Employee, and onboarding approve.",
    ],
    snippet: "settings-employee-code",
  },
  {
    id: "settings-policies-onboarding",
    title: "Settings — Onboarding Policy",
    summary: "Six-step wizard to define how onboarding requests, approvals, documents, and checklists behave.",
    location: "Employee → Settings → Policies → Onboarding",
    features: [
      "Policy list — create, edit, duplicate, deactivate; overlap warnings when applicability clashes.",
      "Step 1 — Policy basics: name, effective date, policy version, auto-assign on joining.",
      "Step 2 — Profile completion: block joining/approval without profile completion, plus per-tab checkboxes (Org, Personal, Family, Education, Professional, Bank, Statutory, Documents) — uncheck a tab to make it optional for approval.",
      "Step 3 — Workflow: approval method (Auto approved / Through workflow); reminder days before joining; escalation days; parallel approval; approval chain steps (New request + Cancellation tabs); notify-only steps. Re-initiation allowed returns rejected cases to draft so HR can fix and resubmit (regenerates the approval chain).",
      "Step 4 — Communication & letters: appointment / offer letter templates and communication options.",
      "Step 5 — Checklist mapping: task name, trigger event, department, action role, mandatory flag, due days (SLA starts after required profile tabs are complete).",
      "Step 6 — Applicability: branch, department, sub-department, designation, grade, category, location, employment type — which employees match this policy.",
      "Policy summary and workflow diagram available before save.",
      "Deactivate (row ⋮ menu) — sets policy inactive; stops matching new onboarding cases; in-flight requests continue on their saved policy snapshot.",
      "Delete (row ⋮ menu) — allowed only when zero in-flight requests (draft or pending approval). If blocked, the modal shows total pending count and counts by approver. Approve or reject each item in Employee → Dashboard → Action Insights, or deactivate instead.",
    ],
    processFlows: [
      {
        title: "Deactivate vs Delete",
        steps: [
          "Open Employee → Settings → Policies → Onboarding → policy row ⋮ menu.",
          "Deactivate: policy status becomes inactive. New onboarding no longer matches this policy. Existing draft/pending requests keep their workflow snapshot.",
          "Delete: system checks in-flight onboarding requests linked to this policy (draft, pending approval).",
          "If any are pending → delete is blocked; modal shows total pending count and counts by approver (RM/HR/CEO).",
          "If none are pending → confirm permanent delete; policy and applicability assignments are removed.",
        ],
      },
    ],
    snippet: "settings-policies-onboarding",
  },
  {
    id: "settings-policies-confirmation",
    title: "Settings — Confirmation Policy",
    summary: "Rules and workflow for probation-to-confirmed transitions.",
    location: "Employee → Settings → Policies → Confirmation",
    features: [
      "Wizard steps: Policy basics → Confirmation rules → Workflow → Communication & letters → Checklist mapping → Applicability.",
      "Confirmation rules — probation period, auto-confirm options, and eligibility criteria.",
      "Workflow — approval chain for confirmation requests (same pattern as onboarding workflow step).",
      "Checklist mapping — post-confirmation tasks assigned to roles.",
      "Applicability — org dimensions that determine which employees use this policy.",
      "Deactivate — retire policy for new confirmation cases; in-flight pending/on-hold requests continue on snapshot.",
      "Delete — blocked while confirmation requests are pending approval or on hold; resolve in Action Insights or deactivate instead.",
    ],
    processFlows: [
      {
        title: "Deactivate vs Delete",
        steps: [
          "Policy row ⋮ → Deactivate sets status inactive; no new employees match this confirmation policy.",
          "In-flight confirmation requests (pending approval, on hold) continue on their saved workflow snapshot.",
          "Delete runs an in-flight check first. Pending count > 0 → blocked with approver/status breakdown.",
          "Pending count = 0 → confirm to permanently remove the policy record.",
        ],
      },
    ],
    snippet: "settings-policies-confirmation",
  },
  {
    id: "settings-policies-transfer",
    title: "Settings — Transfer Policy",
    summary: "Rules and workflow for internal transfers (branch, department, manager, etc.).",
    location: "Employee → Settings → Policies → Transfer",
    features: [
      "Wizard steps: Policy basics → Transfer rules → Workflow → Communication & letters → Checklist mapping → Applicability.",
      "Transfer rules — what org fields may change (branch, department, designation, manager, etc.).",
      "Workflow — Auto approved / Through workflow / On instance; multi-step approvers for transfer and cancellation.",
      "Letters — transfer order / communication templates.",
      "Checklist mapping — tasks spawned when a transfer request is created (completed via Employee Insights).",
      "Employee list → Transfer creates a policy-matched request, starts the approval chain, and appears in Employee Insights.",
      "Deactivate — stops matching new transfers; in-flight draft/pending/on-hold/cancellation-pending requests continue on snapshot.",
      "Delete — blocked while any in-flight transfer request is linked; modal shows pending by approver before allowing permanent delete.",
    ],
    processFlows: [
      {
        title: "Deactivate vs Delete",
        steps: [
          "Deactivate from row ⋮ when you want to retire a transfer policy without disturbing open cases.",
          "Delete checks transfer requests in draft, pending approval, on hold, or cancellation pending.",
          "Blocked delete shows how many cases are waiting with each approver/step.",
          "Clear or complete cases in Employee Action Insights, then delete when the count reaches zero.",
        ],
      },
    ],
    snippet: "settings-policies-transfer",
  },
  {
    id: "settings-policies-separation",
    title: "Settings — Separation Policy",
    summary: "Exit and resignation workflow, clearance checklists, and applicability.",
    location: "Employee → Settings → Policies → Separation",
    features: [
      "Wizard steps: Policy basics → Separation rules → Workflow → Communication & letters → Checklist mapping → Applicability.",
      "Separation rules — notice period, retro-resignation, exit types, clearance requirements.",
      "Workflow — approval chain for resignation / termination / cancellation; optional notice waiver by approver.",
      "Checklist mapping — exit clearance tasks (IT, HR, assets, etc.).",
      "Employees → Actions → Resignation (or Request → Resignation) creates a policy-matched separation request; shortfall and checklist details are under the ⓘ icon. Approvals and checklist Done appear in Employee Insights.",
      "Deactivate — retire policy for new separations; clearance/FnF/pending cases keep their snapshot.",
      "Delete — also respects existing employee-link guard in UI; backend blocks when in-flight separation requests exist (draft, pending, on hold, clearance, FnF, cancellation pending).",
    ],
    processFlows: [
      {
        title: "Deactivate vs Delete",
        steps: [
          "Use Deactivate to stop new resignation/exit cases from matching this policy.",
          "Open separation requests (including clearance and FnF pending) keep running on the saved policy snapshot.",
          "Delete is blocked while in-flight separation requests remain linked to the policy.",
          "Modal shows pending total and counts by approver; approve or reject in Action Insights before deleting.",
        ],
      },
    ],
    snippet: "settings-policies-separation",
  },
  {
    id: "settings-structures",
    title: "Settings — Salary Structures",
    summary: "Manage pay components, structures, and assignment to employees.",
    location: "Employee → Settings → Structures",
    features: [
      "Enhanced salary structure manager — define earnings, deductions, and templates on the Salary Structures tab.",
      "Salary Components panel sits on the right of Structures — add, edit, activate/deactivate, delete in one row per component.",
      "TDS Reference Heads tab — system master of income-tax classification heads. Every salary component must map to exactly one head before save; structures cannot publish with unmapped components.",
      "TDS / Form-16 / exemptions (HRA, LTA, Sec 16 PT, Emp. PF + VPF under 80C, Employer NPS under 80CCD(2), meal voucher, etc.) are driven by the mapped TDS Reference Head — not by component names. Super Admin edits heads; Finance Act changes update head config only.",
      "Structure templates can enable Performance Bonus, Employer NPS (benefit / 80CCD(2)), VPF (employee deduction / 80C), and Meal Voucher (₹200/meal IT Rules 2026) — configure via detail popups.",
      "Org Configuration → Settings controls Default Salary Structure method (CTC / Gross / Both) and renames the employee annual amount field accordingly.",
      "Manual vs Auto: Manual structures need per-component ₹ amounts (eye Save or Payroll → Manual Component Import). Auto (Formula) structures derive from Profile CTC/Gross.",
      "Per-employee Salary Structure (eye): Statutory Applicability + opt-in NPS/VPF/Meal when the structure enables them (detail popups with effective month). Reloads the saved snapshot — Manual heads show entered amounts; Formula / Balance CTC stay calculated. Save requires Subtotal (E) = Profile Gross when structure mode is Gross, or Total CTC (E + B) = Profile CTC when mode is CTC. After joining month is processed or finalised, the profile structure is read-only — use Salary Revision / Update Statutory.",
      "After employee import: Auto structures apply from CTC automatically; Manual employees must get component amounts before Process (silent CTC rebuild is blocked for Manual).",
      "Simulator available for structure testing (where enabled in UI).",
    ],
    snippet: "settings-structures",
  },
  {
    id: "settings-letters",
    title: "Settings — Letters Management",
    summary:
      "Professional letter templates with tag merge, Word salary table, preview, and generation from Settings or the employee row menu.",
    location: "Employee → Settings → Letters Management; Employees → Actions → Generate Letter",
    features: [
      "Create templates by uploading a Word (.docx) file with {{tags}} from Available Tags.",
      "Download Sample Format downloads a single-page .docx of the letter body with {{tags}} only (no extra letterhead).",
      "Generate replaces tags inside your uploaded Word file and downloads a filled .docx — original formatting is preserved.",
      "Salary tags fill from the employee’s active salary structure; {{salary_structure_table}} inserts a Word table with Monthly and Annual columns.",
      "Generate opens a PDF preview where you can Download PDF or Send mail to the employee’s official / personal email.",
      "If neither email is available, Send mail warns: No active email is available to send.",
      "From Employees → Actions → Generate Letter, pick a template for that employee.",
      "Templates sync to the server and are selectable in policy Communication & letters steps.",
    ],
    snippet: "settings-letters",
  },
];
